import { useCallback } from 'react';
import * as THREE from 'three';
import type { WorkspaceState, WorkspaceCallbacks } from '../../utils/workspace.types';
import { generateOffsetNodes, NodePoint } from '@/utilities/generateNodePoints';

/**
 * Node operations hook - handles all node-related functionality
 * Fixed to properly create nodes and handle all interactions
 */
export const useNodeOperations = (
  state: WorkspaceState,
  callbacks: WorkspaceCallbacks,
  modelStore: any,
  uiStore: any
) => {
  // Handle plane click for node creation
  const handlePlaneClick = useCallback((point: THREE.Vector3) => {
    if (!state.isNodeCreationMode) return;

    //console.log('🎯 Plane clicked at:', point);
    
    // Round coordinates to 2 decimal places precision
    const coords = { 
      x: Math.round(point.x * 100) / 100, 
      y: Math.round(point.y * 100) / 100, 
      z: Math.round(point.z * 100) / 100 
    };
    
    // Set coordinates and show dialog
    callbacks.setQuickNodeCoords(coords);
    callbacks.setEditableCoords(coords);
    callbacks.setShowQuickNodeDialog(true);
  }, [state.isNodeCreationMode, callbacks]);

  // Handle node click for selection and beam creation
  const handleNodeClick = useCallback((nodeId: string, _event?: any) => {
    //console.log('🎯 Node clicked:', nodeId, 'beamMode:', state.isBeamCreationMode);
    
    if (state.isBeamCreationMode) {
      // In beam creation mode, handle node selection for beam endpoints
      if (state.selectedNodeId && state.selectedNodeId !== nodeId) {
        // Two nodes selected - create beam
        //console.log('🎯 Creating beam between nodes:', state.selectedNodeId, 'and', nodeId);
        
        if (modelStore?.addBeam) {
          modelStore.addBeam(state.selectedNodeId, nodeId);
          // Clear selection after beam creation
          callbacks.setSelectedNodeId(null);
          
          if (uiStore?.showNotification) {
            uiStore.showNotification('Beam created successfully', 'success');
          }
        }
      } else {
        // First node selection for beam creation
        callbacks.setSelectedNodeId(nodeId);
        //console.log('🎯 First node selected for beam:', nodeId);
      }
    } else {
      // Normal node selection
      if (state.isShiftPressed && modelStore?.toggleNodeSelection) {
        // Multi-select with Shift
        modelStore.toggleNodeSelection(nodeId);
      } else if (modelStore?.selectNode) {
        // Single select
        modelStore.selectNode(nodeId);
        callbacks.setSelectedNodeId(nodeId);
      }
    }
  }, [state.isShiftPressed, state.isBeamCreationMode, state.selectedNodeId, callbacks, modelStore, uiStore]);

  // Handle node right click - open contextual menu for node
  const handleNodeRightClick = useCallback((nodeId: string, event?: any) => {
    //console.log('🎯 Node right-clicked:', nodeId);

    if (event) {
      event.stopPropagation();
      if (event.nativeEvent && typeof event.nativeEvent.preventDefault === 'function') {
        event.nativeEvent.preventDefault(); // Prevent browser menu
      }
    }

    const node = modelStore?.nodes?.find((n: any) => n.id === nodeId);
    if (!node) return;

    // Calculate screen position for menu
    let x = 0, y = 0;
    try {
      const canvas = document.querySelector('canvas');
      if (canvas && event?.clientX != null && event?.clientY != null) {
        x = event.clientX;
        y = event.clientY;
      } else if (event?.nativeEvent?.clientX) {
        x = event.nativeEvent.clientX;
        y = event.nativeEvent.clientY;
      } else if (canvas) {
        const rect = canvas.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }
    } catch (e) {
      // fallback
    }

    if (uiStore && uiStore.openContextMenu) {
      uiStore.openContextMenu({ type: 'node', id: nodeId }, { x, y });
      uiStore.setSelectedNodeId(nodeId);
    }
  }, [modelStore, uiStore]);

  // Handle node double click
  const handleNodeDoubleClick = useCallback((nodeId: string, event?: any) => {
    //console.log('🎯 Node double-clicked:', nodeId);
    
    if (event) {
      event.stopPropagation();
    }
    
    // Could trigger edit mode or other actions
    handleNodeRightClick(nodeId, event);
  }, [handleNodeRightClick]);

  // Animate node to position
  const animateNodeToPosition = useCallback((nodeId: string, newPosition: THREE.Vector3) => {
    //console.log('🎯 Animating node to position:', nodeId, newPosition);
    
    callbacks.setIsAnimatingToPosition(true);
    
    // Simulate animation - in real implementation this would use GSAP or Three.js animation
    setTimeout(() => {
      if (modelStore?.updateNode) {
        modelStore.updateNode(nodeId, {
          x: newPosition.x,
          y: newPosition.y,
          z: newPosition.z
        });
      }
      callbacks.setIsAnimatingToPosition(false);
    }, 500);
  }, [callbacks, modelStore]);

  // Handle node hover
  const handleNodeHover = useCallback((nodeId: string | null, event?: any) => {
    callbacks.setHoveredNodeId(nodeId);
    
    if (nodeId && event) {
      callbacks.setTooltipPosition({
        x: event.clientX || 0,
        y: event.clientY || 0
      });
    }
  }, [callbacks]);

  // Quick node creation confirm - FIXED
  const handleQuickNodeConfirm = useCallback(() => {
    if (!modelStore?.addNode) {
      console.error('addNode function not available in modelStore');
      return;
    }
    
  // CSV batch creation removed (coordinateInputValues deprecated)

    // Single point create (existing behavior)
    const coords = state.editableCoords;
    //console.log('🎯 Creating node at:', coords);

    try {
      // Single create or offset-expanded create
      const offsetEnabled = !!state.quickNodeOffsetEnabled;
      const count = Math.max(1, state.quickNodeOffsetCount || 1);
      const offsets = state.quickNodeOffsetValues || { x: 0, y: 0, z: 0 };

      const nodesToCreate: NodePoint[] = offsetEnabled ? generateOffsetNodes(coords, count, offsets) : [coords];

      let created = 0;
      nodesToCreate.forEach(n => {
        modelStore.addNode({ x: n.x, y: n.y, z: n.z });
        created++;
      });

      // Update UI state
      callbacks.setLastCreatedNodeId(`N${Date.now()}`);
      callbacks.setShowQuickNodeDialog(false);

      // Reset dialog state
      callbacks.setQuickNodeCoords({ x: 0, y: 0, z: 0 });
      callbacks.setEditableCoords({ x: 0, y: 0, z: 0 });

      if (uiStore?.showNotification) {
        uiStore.showNotification(`Created ${created} nodes`, 'success');
      }

      //console.log('\u2705 Node(s) created successfully');
    } catch (error) {
      console.error('Error creating node(s):', error);
      if (uiStore?.showNotification) {
        uiStore.showNotification('Failed to create node(s)', 'error');
      }
    }
  }, [state.editableCoords, callbacks, modelStore, uiStore]);

  // Quick node creation cancel
  const handleQuickNodeCancel = useCallback(() => {
    callbacks.setShowQuickNodeDialog(false);
    callbacks.setQuickNodeCoords({ x: 0, y: 0, z: 0 });
    callbacks.setEditableCoords({ x: 0, y: 0, z: 0 });
  }, [callbacks]);

  // Handle coordinate change in dialog
  const handleCoordChange = useCallback((axis: 'x' | 'y' | 'z', value: number) => {
    const currentCoords = state.editableCoords;
    callbacks.setEditableCoords({
      ...currentCoords,
      [axis]: value
    });
  }, [callbacks, state.editableCoords]);

  return {
    handlePlaneClick,
    handleNodeClick,
    handleNodeRightClick,
    handleNodeDoubleClick,
    animateNodeToPosition,
    handleNodeHover,
    handleQuickNodeConfirm,
    handleQuickNodeCancel,
    handleCoordChange
  };
};
